import { BsEye } from 'react-icons/bs';
import DocumentLinesModal from '../components/documents/DocumentLinesModal';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../utils/api';
import { formatCurrency, formatDate, formatRecurrence } from '../utils/invoiceUtils';

const FacturasRecurrentesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restoredList = location.state?.invoiceNavigation;
  const [linesDocument, setLinesDocument] = useState(null);
  const token = localStorage.getItem('token');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(restoredList?.page || 1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(restoredList?.query?.search || '');
  const [busyId, setBusyId] = useState(null);
  const [totals, setTotals] = useState({ thisMonth: null, nextMonth: null });

  // Configuración del cron + auto-envío (movida desde /facturas).
  const [configOpen, setConfigOpen] = useState(false);
  const [settings, setSettings] = useState({
    cronEnabled: false,
    proformaSeries: '',
    proformaSeriesPad: 4,
    series: '',
    seriesPad: 4,
    autoSendProformas: false,
    autoSendTemplateId: '',
    autoSendAccountId: '',
    autoSendWhatsapp: false,
    autoSendWhatsappTemplate: 'envio_factura',
    defaultDueDays: 30,
    rectificativaSeries: '',
    rectificativaSeriesPad: 4
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [cronRunning, setCronRunning] = useState(false);
  const [cronMessage, setCronMessage] = useState('');

  const totalPages = useMemo(() => Math.max(Math.ceil(total / pageSize), 1), [total, pageSize]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, pageSize });
      params.append('recurrent', 'true');
      if (search) params.append('search', search);
      const res = await apiFetch(`/api/invoices/all?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo facturas recurrentes');
      if (Array.isArray(data)) {
        setInvoices(data);
        setTotal(data.length);
      } else {
        setInvoices(Array.isArray(data.items) ? data.items : []);
        setTotal(Number(data.total || 0));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, token]);

  const loadTotals = useCallback(async () => {
    try {
      const res = await apiFetch('/api/invoices/recurrent-totals', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) setTotals({ thisMonth: Number(data?.thisMonth || 0), nextMonth: Number(data?.nextMonth || 0) });
    } catch (err) {
      console.error('Error cargando totales recurrentes', err);
    }
  }, [token]);

  const togglePaused = async (inv) => {
    setBusyId(inv.id);
    try {
      const res = await apiFetch(`/api/clients/invoices/${inv.id}/recurrent-paused`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ paused: !inv.recurrent_paused })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo actualizar');
      fetchInvoices();
      loadTotals();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const loadSettings = useCallback(async () => {
    try {
      const res = await apiFetch('/api/invoices/settings', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) {
        setSettings({
          cronEnabled: Boolean(data?.cronEnabled),
          proformaSeries: data?.proformaSeries || '',
          proformaSeriesPad: data?.proformaSeriesPad || 4,
          series: data?.series || '',
          seriesPad: data?.seriesPad || 4,
          autoSendProformas: Boolean(data?.autoSendProformas),
          autoSendTemplateId: data?.autoSendTemplateId ? String(data.autoSendTemplateId) : '',
          autoSendAccountId: data?.autoSendAccountId ? String(data.autoSendAccountId) : '',
          autoSendWhatsapp: Boolean(data?.autoSendWhatsapp),
          autoSendWhatsappTemplate: data?.autoSendWhatsappTemplate || 'envio_factura',
          defaultDueDays: data?.defaultDueDays ?? 30,
          rectificativaSeries: data?.rectificativaSeries || '',
          rectificativaSeriesPad: data?.rectificativaSeriesPad || 4
        });
      }
    } catch (err) {
      console.error('Error cargando configuración de facturas', err);
    }
  }, [token]);

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await apiFetch('/api/email/templates', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo plantillas');
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err.message);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const res = await apiFetch('/api/email-accounts', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo cuentas');
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err.message);
    } finally {
      setAccountsLoading(false);
    }
  };

  const openConfig = async () => {
    setCronMessage('');
    await loadSettings();
    await Promise.all([loadTemplates(), loadAccounts()]);
    setConfigOpen(true);
  };

  const runInvoiceCronNow = async () => {
    setCronRunning(true);
    setCronMessage('');
    try {
      const res = await apiFetch('/api/invoices/cron/run?force=1', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error ejecutando cron');
      setCronMessage(`Generadas ${data.generated || 0}, procesadas ${data.processed || 0}, saltadas ${data.skipped || 0}.`);
      fetchInvoices();
      loadTotals();
    } catch (err) {
      setCronMessage(err.message);
    } finally {
      setCronRunning(false);
    }
  };

  const saveSettings = async () => {
    if (settings.autoSendProformas) {
      if (!settings.autoSendTemplateId) return alert('Selecciona una plantilla para el autoenvío.');
      if (!settings.autoSendAccountId) return alert('Selecciona una cuenta de envío para el autoenvío.');
    }
    if (settings.autoSendWhatsapp && !settings.autoSendWhatsappTemplate) {
      return alert('Indica la plantilla de WhatsApp para el autoenvío.');
    }
    setSavingSettings(true);
    try {
      // Enviamos el objeto COMPLETO de settings (incluidas series/proforma/
      // rectificativa tal como se cargaron) para no pisar la numeración que se
      // edita en /facturas.
      const res = await apiFetch('/api/invoices/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setSettings({
        cronEnabled: Boolean(data?.cronEnabled),
        proformaSeries: data?.proformaSeries || '',
        proformaSeriesPad: data?.proformaSeriesPad || settings.proformaSeriesPad,
        series: data?.series || '',
        seriesPad: data?.seriesPad || settings.seriesPad,
        autoSendProformas: Boolean(data?.autoSendProformas),
        autoSendTemplateId: data?.autoSendTemplateId ? String(data.autoSendTemplateId) : '',
        autoSendAccountId: data?.autoSendAccountId ? String(data.autoSendAccountId) : '',
        autoSendWhatsapp: Boolean(data?.autoSendWhatsapp),
        autoSendWhatsappTemplate: data?.autoSendWhatsappTemplate || 'envio_factura',
        defaultDueDays: data?.defaultDueDays ?? settings.defaultDueDays,
        rectificativaSeries: data?.rectificativaSeries || '',
        rectificativaSeriesPad: data?.rectificativaSeriesPad || settings.rectificativaSeriesPad
      });
      setConfigOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    loadTotals();
  }, [loadTotals]);

  return (
    <div className="small">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h2 className="mb-0">Facturas recurrentes</h2>
        {linesDocument && <DocumentLinesModal document={linesDocument} kind="invoices" onClose={() => setLinesDocument(null)} />}
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/facturas/nueva')}>
            Nueva factura recurrente
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/facturas')}>
            Volver a facturas
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={openConfig}>
            Configuración
          </button>
          {loading && <span className="badge bg-info text-dark">Cargando...</span>}
        </div>
      </div>
      <p className="text-muted mb-3">
        Plantillas que generan facturas automáticamente. No son facturas fiscales.
      </p>

      <div className="row g-2 mb-3">
        <div className="col-sm-6 col-lg-4">
          <div className="card shadow-sm h-100 border-primary-subtle">
            <div className="card-body py-2">
              <div className="text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.03em' }}>Total recurrente este mes</div>
              <div className="h4 mb-0 fw-bold">{totals.thisMonth == null ? '—' : formatCurrency(totals.thisMonth)}</div>
              <div className="text-muted" style={{ fontSize: '0.68rem' }}>Base imponible (sin IVA) de las recurrentes activas con próxima renovación este mes</div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-4">
          <div className="card shadow-sm h-100 border-primary-subtle">
            <div className="card-body py-2">
              <div className="text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.03em' }}>Total recurrente mes que viene</div>
              <div className="h4 mb-0 fw-bold">{totals.nextMonth == null ? '—' : formatCurrency(totals.nextMonth)}</div>
              <div className="text-muted" style={{ fontSize: '0.68rem' }}>Base imponible (sin IVA) de las recurrentes activas con próxima renovación el mes que viene</div>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Todas las plantillas recurrentes</h5>
            <div className="text-muted small">Total: {total}</div>
          </div>
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <input
                className="form-control form-control-sm"
                placeholder="Buscar por cliente, email o número"
                value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              />
            </div>
          </div>
          {loading ? (
            <p>Cargando...</p>
          ) : invoices.length ? (
            <div className="table-responsive">
              <table className="table table-striped table-sm align-middle">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Número</th>
                    <th>Periodicidad</th>
                    <th>Próxima renovación</th>
                    <th>Base (sin IVA)</th>
                    <th>Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} style={inv.recurrent_paused ? { opacity: 0.6 } : undefined}>
                      <td>
                        {inv.client_id ? (
                          <Link to={`/clientes/${inv.client_id}`} className="text-decoration-none">
                            {inv.razon_social || `Cliente #${inv.client_id}`}
                          </Link>
                        ) : (inv.razon_social || '-')}
                      </td>
                      <td>{inv.numero || inv.serie || `#${inv.id}`}</td>
                      <td>{formatRecurrence(inv.recurrent_unit, inv.recurrent_interval)}</td>
                      <td>{formatDate(inv.recurrent_next_date)}</td>
                      <td>{formatCurrency(inv.subtotal)}</td>
                      <td>
                        {inv.recurrent_paused
                          ? <span className="badge bg-secondary">Desactivada</span>
                          : <span className="badge bg-success">Activa</span>}
                      </td>
                      <td className="text-end">
                        <button
                          className={`btn btn-sm me-2 ${inv.recurrent_paused ? 'btn-outline-success' : 'btn-outline-warning'}`}
                          type="button"
                          disabled={busyId === inv.id}
                          onClick={() => togglePaused(inv)}
                        >
                          {busyId === inv.id ? '…' : (inv.recurrent_paused ? 'Activar' : 'Desactivar')}
                        </button>
<button type="button" className="btn btn-sm btn-outline-secondary me-1" title="Ver líneas" aria-label={`Ver líneas de plantilla ${inv.numero || inv.id}`} onClick={() => setLinesDocument(inv)}><BsEye /></button>                        <button
                          className="btn btn-sm btn-outline-secondary"
                          type="button"
                          onClick={() => navigate(`/facturas/${inv.id}`, { state: { invoiceNavigation: { ids: invoices.map((item) => item.id), page, pageSize, total, query: { pageSize, recurrent: 'true', search }, returnTo: '/facturas-recurrentes' } } })}
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No hay facturas recurrentes.</p>
          )}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">Página {page} de {totalPages} (total {total})</div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>

      {configOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Configuración de facturas recurrentes</h5>
                <button className="btn-close" onClick={() => setConfigOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="recurrentCronCheck"
                    checked={settings.cronEnabled}
                    onChange={(e) => setSettings((p) => ({ ...p, cronEnabled: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="recurrentCronCheck">
                    Activar cron diario (10:00)
                  </label>
                  <div className="text-muted small">
                    El cron genera facturas en BORRADOR a partir de las recurrentes activas (no las pausadas).
                  </div>
                </div>
                <div className="mb-3">
                  <button className="btn btn-outline-primary btn-sm" onClick={runInvoiceCronNow} disabled={cronRunning}>
                    {cronRunning ? 'Generando...' : 'Generar ahora'}
                  </button>
                  {cronMessage && <div className="text-muted small mt-1">{cronMessage}</div>}
                </div>

                <hr />

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="recurrentAutoSendCheck"
                    checked={settings.autoSendProformas}
                    onChange={(e) => setSettings((p) => ({
                      ...p,
                      autoSendProformas: e.target.checked,
                      autoSendTemplateId: e.target.checked ? p.autoSendTemplateId : '',
                      autoSendAccountId: e.target.checked ? p.autoSendAccountId : ''
                    }))}
                  />
                  <label className="form-check-label" htmlFor="recurrentAutoSendCheck">
                    Enviar facturas automáticamente por email
                  </label>
                  <div className="text-muted small">Envía el PDF al email principal del cliente.</div>
                </div>
                {settings.autoSendProformas && (
                  <>
                    <div className="mb-3">
                      <label className="form-label small">Plantilla de email</label>
                      <select
                        className="form-select"
                        value={settings.autoSendTemplateId}
                        onChange={(e) => setSettings((p) => ({ ...p, autoSendTemplateId: e.target.value }))}
                        disabled={templatesLoading}
                      >
                        <option value="">{templatesLoading ? 'Cargando...' : 'Selecciona una plantilla'}</option>
                        {templates.map((tpl) => (
                          <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label small">Cuenta de envío (SMTP)</label>
                      <select
                        className="form-select"
                        value={settings.autoSendAccountId}
                        onChange={(e) => setSettings((p) => ({ ...p, autoSendAccountId: e.target.value }))}
                        disabled={accountsLoading}
                      >
                        <option value="">{accountsLoading ? 'Cargando...' : 'Selecciona una cuenta'}</option>
                        {accounts.filter((acc) => acc?.active !== false).map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.from_name || acc.name || 'Cuenta'} {acc.from_email ? `(${acc.from_email})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="recurrentAutoSendWhatsappCheck"
                    checked={settings.autoSendWhatsapp}
                    onChange={(e) => setSettings((p) => ({
                      ...p,
                      autoSendWhatsapp: e.target.checked,
                      autoSendWhatsappTemplate: e.target.checked ? (p.autoSendWhatsappTemplate || 'envio_factura') : p.autoSendWhatsappTemplate
                    }))}
                  />
                  <label className="form-check-label" htmlFor="recurrentAutoSendWhatsappCheck">
                    Enviar facturas automáticamente por WhatsApp
                  </label>
                  <div className="text-muted small">Envía el PDF al teléfono principal del cliente.</div>
                </div>
                {settings.autoSendWhatsapp && (
                  <div className="mb-3">
                    <label className="form-label small">Plantilla de WhatsApp</label>
                    <input
                      className="form-control"
                      value={settings.autoSendWhatsappTemplate}
                      onChange={(e) => setSettings((p) => ({ ...p, autoSendWhatsappTemplate: e.target.value }))}
                      placeholder="envio_factura"
                    />
                    <div className="text-muted small">Debe ser una plantilla aprobada en Meta.</div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setConfigOpen(false)}>Cancelar</button>
                <button className="btn btn-primary" disabled={savingSettings} onClick={saveSettings}>
                  {savingSettings ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacturasRecurrentesPage;
